const PRODUCT_PAGE_SELECTOR = '.product-information[data-testid="product-information"]';
const PRODUCT_RECOMMENDATIONS_SELECTOR = 'product-recommendations';
const RECOMMENDATION_IMAGE_PREVIEW_WIDTH = 900;
const RECOMMENDATION_IMAGE_FULL_WIDTH = 1800;
const PRODUCT_IMAGE_PREVIEW_WIDTH = 1200;
const PRODUCT_IMAGE_FULL_WIDTH = 2200;
const PRODUCT_IMAGE_POPUP_MOBILE_QUERY = '(max-width: 749px), (pointer: coarse) and (max-height: 500px)';
const PRODUCT_GUIDE_SIZE_OPTION_SELECTOR = [
  '.variant-option--size',
  '.variant-option--sizes',
  '.variant-option--sizing',
  '.variant-option--deck-size',
  '.variant-option--board-size',
  '.variant-option--width',
].join(', ');
const productQuickViewCache = new Map();
const modalImagePreloadCache = new Map();
let modalImageSourceRequestId = 0;
let productRecommendationsObserver = null;
let recommendationPrefetchObserver = null;
let recommendationQuickViewBound = false;
let productImageModalBound = false;
let productHelpDrawerBound = false;
let productPageScrollStateFrame = 0;
const productAddonTitleCollections = new Set();
let productAddonTitleResizeBound = false;
let productAddonTitleResizeFrame = 0;

const recommendationQuickViewState = {
  modal: null,
  activeProduct: null,
  activeImageIndex: 0,
  requestId: 0,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
  swipe: null,
};

const productImageModalState = {
  modal: null,
  images: [],
  activeImageIndex: 0,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
  swipe: null,
};

const productHelpDrawerState = {
  modal: null,
  lastFocusedElement: null,
  scrollY: 0,
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverflow: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
};

const productInfoOverlayState = {
  modal: null,
  controller: null,
  scrollY: 0,
  previousDocumentScrollBehavior: '',
  previousDocumentOverflow: '',
  previousDocumentOverscrollBehavior: '',
  previousDocumentScrollbarGutter: '',
  previousBodyOverscrollBehavior: '',
  previousBodyPaddingRight: '',
  inertedElements: [],
  touchX: 0,
  touchY: 0,
};
const productInfoOverlayControllers = new WeakMap();
const productInfoOverlayElements = new Set();
let productInfoOverlayConnectionObserver = null;

function bindDescriptionScrollRelease(description) {
  if (!(description instanceof HTMLElement) || description.dataset.hpScrollReleaseBound === 'true') return;

  description.dataset.hpScrollReleaseBound = 'true';
  let lastY = 0;
  const shouldReleaseToPage = (deltaY) => {
    if (Math.abs(deltaY) < 1) return false;
    if (description.scrollHeight <= description.clientHeight + 1) return false;

    const atTop = description.scrollTop <= 1;
    const atBottom = description.scrollTop + description.clientHeight >= description.scrollHeight - 1;
    return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
  };

  description.addEventListener(
    'touchstart',
    (event) => {
      lastY = event.touches?.[0]?.clientY || 0;
    },
    { passive: true }
  );

  description.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaY = lastY - touch.clientY;
      lastY = touch.clientY;

      if (!shouldReleaseToPage(deltaY)) return;

      event.preventDefault();
      window.scrollBy(0, deltaY);
    },
    { passive: false }
  );

  description.addEventListener(
    'wheel',
    (event) => {
      if (!shouldReleaseToPage(event.deltaY)) return;

      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' });
    },
    { passive: false }
  );
}

function enhanceProductGalleryLayout(productInfo) {
  const containers = Array.from(
    productInfo.querySelectorAll('.product-information__media .media-gallery__grid .product-media-container--image')
  ).filter((container) => container instanceof HTMLElement);

  containers.forEach((container) => {
    container.classList.remove('hp-product-gallery__more');
    container.removeAttribute('data-hp-gallery-more-label');
  });

  const hiddenCount = Math.max(containers.length - 5, 0);
  if (hiddenCount <= 0) return;

  const moreContainer = containers[4];
  moreContainer.classList.add('hp-product-gallery__more');
  moreContainer.setAttribute('data-hp-gallery-more-label', `+${hiddenCount} MORE`);
}

function bindProductGalleryLayoutEnhancements(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpGalleryLayoutBound === 'true') return;

  productInfo.dataset.hpGalleryLayoutBound = 'true';
  enhanceProductGalleryLayout(productInfo);

  if (typeof MutationObserver !== 'function') return;

  const observer = new MutationObserver(() => {
    enhanceProductGalleryLayout(productInfo);
    scheduleProductDetailHeightSync(productInfo);
  });
  observer.observe(productInfo, { childList: true, subtree: true });
  productInfo.hpGalleryLayoutObserver = observer;
}

function bindProductGalleryMobileArrows(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  productInfo.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.hpMobileArrowsBound === 'true') return;

    const scroller = gallery.querySelector('slideshow-slides');
    const container = gallery.querySelector('slideshow-container');
    if (!(scroller instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    const slideCount = Array.from(scroller.children).filter((child) =>
      child instanceof HTMLElement && child.matches('slideshow-slide, .product-media-container')
    ).length;
    if (slideCount <= 1) return;

    gallery.dataset.hpMobileArrowsBound = 'true';
    const previous = createProductGalleryArrow('prev');
    const next = createProductGalleryArrow('next');
    container.append(previous, next);

    const update = () => updateProductGalleryArrowState(scroller, previous, next, slideshow);
    const move = (direction, event) => {
      const maxIndex = getProductGalleryArrowMaxIndex(scroller, slideshow);
      const currentIndex = getProductGalleryArrowIndex(scroller, slideshow);
      const nextIndex = Math.max(0, Math.min(maxIndex, currentIndex + direction));

      if (typeof slideshow?.select === 'function') {
        // Share the native pagination state so dots and swipes can interrupt arrows.
        slideshow.select(nextIndex, event);
      } else {
        const slideWidth = Math.max(scroller.clientWidth, 1);
        const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
        const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextIndex * slideWidth));
        scroller.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
      }

      update();
    };

    previous.addEventListener('click', (event) => {
      event.preventDefault();
      move(-1, event);
    });
    next.addEventListener('click', (event) => {
      event.preventDefault();
      move(1, event);
    });
    scroller.addEventListener('scroll', update, { passive: true });
    slideshow?.addEventListener?.('slideshow:select', () => {
      update();
      // Native drag selection emits before assigning its current index.
      window.requestAnimationFrame(update);
    });
    window.addEventListener('resize', update, { passive: true });
    update();
    window.requestAnimationFrame(update);
  });
}

function createProductGalleryArrow(direction) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `hp-product-gallery-arrow hp-product-gallery-arrow--${direction}`;
  button.setAttribute('aria-label', direction === 'next' ? 'Next product image' : 'Previous product image');
  button.innerHTML = direction === 'next'
    ? '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>'
    : '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>';
  return button;
}

function getProductGalleryArrowIndex(scroller, slideshow) {
  if (typeof slideshow?.select === 'function' && Number.isFinite(slideshow.current)) return slideshow.current;

  const slideWidth = Math.max(scroller.clientWidth, 1);
  return Math.round(scroller.scrollLeft / slideWidth);
}

function getProductGalleryArrowMaxIndex(scroller, slideshow) {
  if (typeof slideshow?.select === 'function' && slideshow.slides) return Math.max(slideshow.slides.length - 1, 0);

  const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
  const slideWidth = Math.max(scroller.clientWidth, 1);
  return Math.max(Math.round(maxScrollLeft / slideWidth), 0);
}

function updateProductGalleryArrowState(scroller, previous, next, slideshow) {
  const maxIndex = getProductGalleryArrowMaxIndex(scroller, slideshow);
  const currentIndex = Math.max(0, Math.min(maxIndex, getProductGalleryArrowIndex(scroller, slideshow)));
  previous.disabled = currentIndex <= 0;
  next.disabled = currentIndex >= maxIndex;
}

function getProductGalleryPosition(productInfo) {
  const gallery = productInfo.querySelector('.product-information__media media-gallery');
  const scroller = gallery?.querySelector('slideshow-slides');
  const slideshow = gallery?.slideshow || gallery?.querySelector('slideshow-component');

  return {
    scroller: scroller instanceof HTMLElement ? scroller : null,
    slideshow,
    scrollLeft: scroller instanceof HTMLElement ? scroller.scrollLeft : 0,
    current: Number.isFinite(slideshow?.current) ? slideshow.current : null,
  };
}

function restoreProductGalleryPosition(position) {
  if (!position?.scroller && !position?.slideshow) return;

  const restore = () => {
    if (position.slideshow && Number.isFinite(position.current) && typeof position.slideshow.select === 'function') {
      position.slideshow.select(position.current, undefined, { animate: false });
    }
    if (position.scroller) position.scroller.scrollLeft = position.scrollLeft || 0;
  };

  window.requestAnimationFrame(restore);
  window.setTimeout(restore, 80);
  window.setTimeout(restore, 240);
}

function bindProductVariantGalleryStability(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpVariantGalleryStable === 'true') return;

  productInfo.dataset.hpVariantGalleryStable = 'true';
  let latestPosition = null;
  const capture = () => {
    latestPosition = getProductGalleryPosition(productInfo);
  };
  const restore = () => {
    if (latestPosition) restoreProductGalleryPosition(latestPosition);
    scheduleProductDetailHeightSync(productInfo);
  };

  productInfo.addEventListener('change', capture, true);
  productInfo.addEventListener('variant:selected', capture, true);
  productInfo.addEventListener('variant:update', restore, true);
}

function parseProductGuideDeckWidth(value) {
  const match = String(value || '')
    .trim()
    .match(/(?:^|[^\d.])((?:[5-9](?:\.\d+)?)|(?:10(?:\.0+)?))\s*(?:["″”]|$)/i);
  if (!match) return null;

  const width = Number.parseFloat(match[1]);
  return Number.isFinite(width) && width >= 5 && width <= 10 ? width : null;
}

function getSelectedProductGuideSize(variantPicker) {
  if (!(variantPicker instanceof HTMLElement)) return '';

  const optionGroup = variantPicker.querySelector(PRODUCT_GUIDE_SIZE_OPTION_SELECTOR);
  if (!(optionGroup instanceof HTMLElement)) return '';

  const select = optionGroup.querySelector('select');
  if (select instanceof HTMLSelectElement) return select.value;

  const checkedInput = optionGroup.querySelector('input:checked');
  if (checkedInput instanceof HTMLInputElement) return checkedInput.value;

  const singleValue = optionGroup.querySelector('[data-testid="variant-option-single-value"]');
  return singleValue?.textContent?.trim() || '';
}

function getProductGuideRiderFit(width) {
  if (width <= 7.75) {
    return {
      rider: 'Kids & smaller riders',
      shoes: 'Typical shoe range: US Men’s 6 and below',
    };
  }
  if (width <= 8) {
    return {
      rider: 'Teens & adults',
      shoes: 'Typical shoe range: US Men’s 6–9',
    };
  }
  if (width <= 8.25) {
    return {
      rider: 'Adults',
      shoes: 'Typical shoe range: US Men’s 9–11',
    };
  }
  return {
    rider: 'Adults',
    shoes: 'Typical shoe range: US Men’s 12+',
  };
}

function formatProductGuideDeckWidth(width) {
  if (!Number.isFinite(width)) return '';
  return Number.isInteger(width) ? width.toFixed(1) : String(width);
}

function productGuideDescriptionLines(rawHtml) {
  const parsed = new DOMParser().parseFromString(String(rawHtml || ''), 'text/html');
  const breaks = new Set(['br', 'p', 'div', 'li', 'ul', 'ol', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
  const parts = [];
  const visit = (node) => {
    if (node.nodeType === 3) {
      parts.push(node.textContent);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.localName;
    if (tag === 'script' || tag === 'style') return;
    if (breaks.has(tag)) parts.push('\n');
    else if (tag === 'td' || tag === 'th') parts.push(' ');
    node.childNodes.forEach(visit);
    if (breaks.has(tag)) parts.push('\n');
  };
  visit(parsed.body);
  return parts.join('').split(/\r\n|[\n\r\u2028\u2029]/)
    .map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
}

function normalizeProductGuideNumber(value) {
  const [integer, fraction = ''] = String(value).split('.');
  const decimal = fraction.replace(/0+$/, '');
  return integer.replace(/^0+(?=\d)/, '') + (decimal ? `.${decimal}` : '');
}

function productGuideSizeKey(value) {
  const match = String(value || '').match(/^\s*([5-9](?:\.\d+)?|10(?:\.0+)?)\s*["″”]?\s*$/i);
  if (!match) return '';
  const normalized = normalizeProductGuideNumber(match[1]);
  return normalized.includes('.') ? normalized : `${normalized}.0`;
}

function productGuideWheelValues(value) {
  const text = String(value || '');
  return {
    diameters: new Set(Array.from(text.matchAll(/(?<![\d.])(\d+(?:\.\d+)?)\s*mm\b/gi),
      (match) => normalizeProductGuideNumber(match[1]))),
    hardnesses: new Set(Array.from(text.matchAll(/(?<![\d.])(\d+(?:\.\d+)?)\s*([ab])\b/gi),
      (match) => normalizeProductGuideNumber(match[1]) + match[2].toLowerCase())),
  };
}

// Keep this read-only contract aligned with scripts/product_guide_specs.py.
// No partial map may supply a different size's wheel facts.
function extractProductGuideVariantWheelSpecs(product, lines = productGuideDescriptionLines(product.body_html)) {
  const setupLike = /(?:\bsetup\s*:|\bsetup\s*$|\d\s*["″”]?\s+setup\b)/i;
  const setupHeading = /^\s*([5-9](?:\.\d+)?|10(?:\.0+)?)\s*["″”]\s+setup\s*:?\s*$/i;
  if (!lines.some((line) => setupLike.test(line))) return { status: 'none', specs: [], reasons: [] };
  const review = (reason) => ({ status: 'review', specs: [], reasons: [reason] });
  const sizeNames = new Set(['size', 'sizes', 'sizing', 'deck size', 'board size', 'width']);
  const options = (product.options || []).filter((option) =>
    sizeNames.has(String(option.name || '').toLowerCase().replace(/\s+/g, ' ').trim())
  );
  if (options.length !== 1) return review('Size-specific setups need exactly one Size option');
  const sizes = (options[0].values || []).map(productGuideSizeKey);
  if (sizes.length < 2 || sizes.some((size) => !size) || new Set(sizes).size !== sizes.length) {
    return review('Size-specific setups need distinct, readable Shopify sizes');
  }

  const sections = new Map();
  let active = '';
  for (const line of lines) {
    const heading = line.match(setupHeading);
    if (heading) {
      active = productGuideSizeKey(heading[1]);
      if (!sizes.includes(active)) return review(`Setup size ${active} does not match a Shopify size`);
      if (sections.has(active)) return review(`More than one setup section for size ${active}`);
      sections.set(active, []);
    } else if (setupLike.test(line)) {
      return review('A setup heading does not contain one readable inch size');
    } else if (/^(?:board\s+dimensions\b|dimensions\b|about\b|collection\s+options\b|delivery\b|meet[ -]?up\b)/i.test(line)) {
      active = '';
    } else if (active) {
      const wheels = line.match(/^\s*[-•–]?\s*wheels?\s*:\s*(.+)$/i);
      if (wheels) sections.get(active).push(wheels[1]);
      else if (!/^\s*[-•–]?\s*(?:deck|trucks?|bearings?|hardware|grip(?:\s+tape)?)\s*:/i.test(line)) {
        const continuation = productGuideWheelValues(line);
        if (continuation.diameters.size || continuation.hardnesses.size) {
          return review(`Size ${active} has ambiguous wheel specification continuation`);
        }
        active = '';
      }
    }
  }
  if (sections.size !== sizes.length) return review('A Shopify size is missing its setup section');

  const specs = [];
  for (const size of sizes) {
    const rows = sections.get(size);
    if (rows.length !== 1) return review(`Size ${size} needs exactly one labelled Wheels line`);
    const { diameters, hardnesses } = productGuideWheelValues(rows[0]);
    if (diameters.size !== 1 || hardnesses.size !== 1) {
      return review(`Size ${size} needs one wheel diameter and one wheel hardness`);
    }
    const [diameter] = diameters;
    const [hardness] = hardnesses;
    if (!(Number(diameter) >= 45 && Number(diameter) <= 70)) {
      return review(`Size ${size} has an unsupported wheel diameter`);
    }
    if (!(Number(hardness.slice(0, -1)) >= 70 && Number(hardness.slice(0, -1)) <= 105)) {
      return review(`Size ${size} has an unsupported wheel hardness`);
    }
    specs.push({ size, wheel_diameter: diameter, wheel_hardness: hardness });
  }
  const title = productGuideWheelValues(product.title);
  const mappedDiameters = new Set(specs.map((spec) => spec.wheel_diameter));
  const mappedHardnesses = new Set(specs.map((spec) => spec.wheel_hardness));
  if (title.diameters.size && (title.diameters.size !== mappedDiameters.size
    || Array.from(title.diameters).some((value) => !mappedDiameters.has(value)))) {
    return review('Wheel diameters in the title conflict with size-specific setups');
  }
  if (title.hardnesses.size && (title.hardnesses.size !== mappedHardnesses.size
    || Array.from(title.hardnesses).some((value) => !mappedHardnesses.has(value)))) {
    return review('Wheel hardness in the title conflicts with size-specific setups');
  }
  return { status: 'ready', specs, reasons: [] };
}

function getProductGuideWheelGuidance(spec) {
  const diameter = Number(spec.wheel_diameter);
  const hardness = Number(spec.wheel_hardness.slice(0, -1));
  const isHardWheel = spec.wheel_hardness.endsWith('b') || hardness >= 97;
  return {
    greatFor: diameter <= 52 || (diameter <= 54 && isHardWheel)
      ? 'Tricks' : diameter <= 54 ? 'Tricks & cruising' : 'Cruising',
    groundType: spec.wheel_hardness.endsWith('b') || hardness >= 99
      ? 'Smooth surfaces' : hardness < 90 ? 'Rough surfaces' : 'Smooth & rough surfaces',
  };
}

function setProductGuideWheelChoice(row, value, type) {
  if (!row) return;
  const label = row.querySelector('[data-hp-guide-wheel-value]');
  const scale = row.querySelector('[data-hp-guide-wheel-scale]');
  if (!label || !scale) return;
  const choices = type === 'great_for'
    ? [['Tricks', 'Tricks'], ['Cruising', 'Cruising'], ['Tricks & cruising', 'Tricks + cruising']]
    : [['Rough surfaces', 'Rough'], ['Smooth surfaces', 'Smooth'], ['Smooth & rough surfaces', 'Both surfaces']];
  label.textContent = value;
  scale.className = 'hp-product-guide__scale';
  scale.setAttribute('role', 'list');
  scale.setAttribute('aria-label', `${type === 'great_for' ? 'Great for' : 'Surface type'} options; current choice: ${value}`);
  scale.replaceChildren(...choices.map(([choice, text]) => {
    const option = document.createElement('span');
    option.className = `hp-product-guide__scale-option${choice === value ? ' is-current' : ''}`;
    option.setAttribute('role', 'listitem');
    if (choice === value) option.setAttribute('aria-current', 'true');
    option.textContent = text;
    return option;
  }));
  row.hidden = false;
}

const productGuideVariantSpecCache = new WeakMap();

function updateProductGuideVariantWheels(productInfo) {
  productInfo.querySelectorAll('hp-product-guide').forEach((guide) => {
    const source = guide.querySelector('template[data-hp-guide-variant-source]');
    if (!source) return;
    const payload = source.content.textContent;
    const cached = productGuideVariantSpecCache.get(source);
    let result = cached?.payload === payload ? cached.result : null;
    if (!result) {
      try {
        result = extractProductGuideVariantWheelSpecs(JSON.parse(payload));
      } catch {
        result = { status: 'review', specs: [], reasons: [] };
      }
      productGuideVariantSpecCache.set(source, { payload, result });
    }
    const bySize = new Map(result.specs.map((spec) => [spec.size, spec]));
    guide.querySelectorAll('[data-hp-guide-size]').forEach((section) => {
      const fallback = section.querySelector('[data-hp-guide-wheel-fallback]');
      const useRow = section.querySelector('[data-hp-guide-wheel-use]');
      const groundRow = section.querySelector('[data-hp-guide-wheel-ground]');
      if (useRow) useRow.hidden = true;
      if (groundRow) groundRow.hidden = true;
      if (fallback) fallback.hidden = false;
      const spec = result.status === 'ready' ? bySize.get(productGuideSizeKey(section.dataset.hpGuideSize)) : null;
      if (!spec) return;
      const guidance = getProductGuideWheelGuidance(spec);
      setProductGuideWheelChoice(useRow, guidance.greatFor, 'great_for');
      setProductGuideWheelChoice(groundRow, guidance.groundType, 'ground_type');
      if (fallback) fallback.hidden = true;
    });
  });
}

function updateProductGuideVariantFit(productInfo) {
  const variantPicker = productInfo.querySelector('variant-picker');
  productInfo.querySelectorAll('hp-product-guide[data-has-size-option="true"]').forEach((guide) => {
    if (!(guide instanceof HTMLElement)) return;

    const selectedWidth = parseProductGuideDeckWidth(getSelectedProductGuideSize(variantPicker));
    const fallbackWidth = parseProductGuideDeckWidth(guide.dataset.fallbackDeckWidth);
    const width = selectedWidth ?? fallbackWidth;
    const riderRow = guide.querySelector('[data-hp-guide-rider-fit-row]');

    if (!Number.isFinite(width)) {
      if (riderRow instanceof HTMLElement) riderRow.hidden = true;
      return;
    }

    guide.dataset.selectedDeckWidth = formatProductGuideDeckWidth(width);
    const fit = getProductGuideRiderFit(width);
    const riderLabel = guide.querySelector('[data-hp-guide-rider-fit]');
    const shoeLabel = guide.querySelector('[data-hp-guide-shoe-fit]');

    if (riderRow instanceof HTMLElement) riderRow.hidden = false;
    if (riderLabel instanceof HTMLElement) riderLabel.textContent = fit.rider;
    if (shoeLabel instanceof HTMLElement) shoeLabel.textContent = `(${fit.shoes})`;
  });
}

function bindProductGuideVariantFit(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpProductGuideFitBound === 'true') return;
  if (!productInfo.querySelector('hp-product-guide[data-has-size-option="true"]')) return;

  productInfo.dataset.hpProductGuideFitBound = 'true';
  const update = () => {
    updateProductGuideVariantFit(productInfo);
    updateProductGuideVariantWheels(productInfo);
    scheduleProductDetailHeightSync(productInfo);
  };

  productInfo.addEventListener('change', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
    window.requestAnimationFrame(update);
  });
  productInfo.addEventListener('variant:update', update);
  update();
}

function requestProductIdleCallback(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 1200 });
    return;
  }

  window.setTimeout(callback, 120);
}

function setImageFetchPriority(image, priority = 'auto') {
  if (!(image instanceof HTMLImageElement)) return;

  if ('fetchPriority' in image) image.fetchPriority = priority;
  else image.setAttribute('fetchpriority', priority);
}

function preloadImageSrc(src, priority = 'auto') {
  const normalizedSrc = src ? String(src) : '';
  if (!normalizedSrc) return Promise.resolve();
  if (modalImagePreloadCache.has(normalizedSrc)) return modalImagePreloadCache.get(normalizedSrc);

  const image = new Image();
  image.decoding = 'async';
  setImageFetchPriority(image, priority);

  let resolveReady;
  const ready = new Promise((resolve) => {
    resolveReady = resolve;
    image.onload = resolve;
    image.onerror = resolve;
  });

  image.src = normalizedSrc;
  if (image.complete) resolveReady();

  const decoded = ready
    .then(() => {
      if (typeof image.decode !== 'function') return undefined;
      return image.decode().catch(() => undefined);
    })
    .catch(() => undefined);

  modalImagePreloadCache.set(normalizedSrc, decoded);
  return decoded;
}

function getSizedShopifyImageSrc(src, width) {
  const normalizedSrc = normalizeRecommendationImageSrc(src);
  if (!normalizedSrc || !width) return normalizedSrc;

  try {
    const url = new URL(normalizedSrc, window.location.origin);
    if (url.hostname.includes('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) {
      url.searchParams.set('width', String(width));
      return url.toString();
    }
  } catch (error) {
    return normalizedSrc;
  }

  return normalizedSrc;
}

function setModalImageSource(
  image,
  imageData,
  sizes = 'min(96vw, 70rem)',
  previewWidth = RECOMMENDATION_IMAGE_PREVIEW_WIDTH,
  fullWidth = RECOMMENDATION_IMAGE_FULL_WIDTH
) {
  if (!(image instanceof HTMLImageElement) || !imageData) return;

  const previewSrc = imageData.previewSrc || imageData.src || '';
  const fullSrc = imageData.src || previewSrc;
  const nextSrc = previewSrc || fullSrc;
  const requestId = String(++modalImageSourceRequestId);

  image.dataset.hpModalSourceRequest = requestId;
  image.hidden = !nextSrc;
  if (image.hidden) {
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    delete image.dataset.hpModalPreviewSrc;
    delete image.dataset.hpModalFullSrc;
    return;
  }

  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
  image.dataset.hpModalPreviewSrc = nextSrc;
  delete image.dataset.hpModalFullSrc;
  image.decoding = 'async';
  image.loading = 'eager';
  setImageFetchPriority(image, 'high');
  if (image.getAttribute('src') !== nextSrc) image.src = nextSrc;
  image.alt = imageData.alt || '';

  if (!fullSrc || fullSrc === nextSrc) return;

  preloadImageSrc(fullSrc).then(() => {
    if (image.dataset.hpModalSourceRequest !== requestId) return;

    image.srcset = `${nextSrc} ${previewWidth}w, ${fullSrc} ${fullWidth}w`;
    image.sizes = sizes;
    image.dataset.hpModalFullSrc = fullSrc;
  });
}

function preloadNeighborImages(images, index) {
  [index + 1, index - 1, index + 2].forEach((nextIndex) => {
    const image = images[nextIndex];
    if (!image) return;
    const previewSrc = image.previewSrc || image.src;
    const fullSrc = image.src || previewSrc;

    preloadImageSrc(previewSrc);
    if (fullSrc && fullSrc !== previewSrc) preloadImageSrc(fullSrc, 'low');
  });
}

function getProductGalleryImageData(gallery) {
  if (!(gallery instanceof HTMLElement)) return [];

  const containers = Array.from(
    gallery.querySelectorAll('.media-gallery__grid .product-media-container--image, slideshow-slide.product-media-container--image')
  );
  const seen = new Set();

  return containers.map(getProductImageDataFromContainer).filter((image) => {
    if (!image?.src || seen.has(image.key)) return false;
    seen.add(image.key);
    return true;
  });
}

function preloadProductGalleryImagesAround(gallery, index = 0, priority = 'auto') {
  const images = getProductGalleryImageData(gallery);
  if (!images.length) return;

  [index, index + 1, index - 1, index + 2].forEach((nextIndex) => {
    const image = images[nextIndex];
    if (!image) return;

    const previewSrc = image.previewSrc || image.src;
    const fullSrc = image.src || previewSrc;

    preloadImageSrc(previewSrc, priority);
    if (fullSrc && fullSrc !== previewSrc) preloadImageSrc(fullSrc, 'low');
  });
}

function bindProductGalleryImagePreloading(productInfo) {
  if (!(productInfo instanceof HTMLElement)) return;

  productInfo.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.hpImagePreloadBound === 'true') return;

    const scroller = gallery.querySelector('slideshow-slides');
    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');
    gallery.dataset.hpImagePreloadBound = 'true';

    const getCurrentIndex = () => {
      if (Number.isFinite(slideshow?.current)) return slideshow.current;
      if (scroller instanceof HTMLElement) {
        return Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1));
      }
      return 0;
    };

    const prime = (index = getCurrentIndex(), priority = 'auto') => {
      preloadProductGalleryImagesAround(gallery, Math.max(0, index), priority);
    };

    let preloadFrame = 0;
    const schedulePrime = () => {
      if (preloadFrame) return;

      preloadFrame = window.requestAnimationFrame(() => {
        preloadFrame = 0;
        prime();
      });
    };

    requestProductIdleCallback(() => prime(0));
    gallery.addEventListener('pointerenter', () => prime(), { passive: true });
    gallery.addEventListener('focusin', () => prime());
    scroller?.addEventListener?.('touchstart', () => prime(getCurrentIndex(), 'high'), { passive: true });
    scroller?.addEventListener?.('pointerdown', () => prime(getCurrentIndex(), 'high'), { passive: true });
    scroller?.addEventListener?.('scroll', schedulePrime, { passive: true });
    slideshow?.addEventListener?.('slideshow:select', (event) => {
      const index = Number(event?.detail?.index);
      prime(Number.isFinite(index) ? index : getCurrentIndex(), 'high');
    });
  });
}

function getProductInfoRoots(root = document) {
  if (root instanceof HTMLElement && root.matches(PRODUCT_PAGE_SELECTOR)) return [root];
  return Array.from(root.querySelectorAll(PRODUCT_PAGE_SELECTOR));
}

function resetProductDetailHeight(productInfo) {
  const detailCard = productInfo.querySelector('.product-details > .group-block');
  productInfo.style.removeProperty('--hp-product-panel-match-height');
  if (detailCard instanceof HTMLElement) detailCard.style.removeProperty('--hp-product-detail-match-height');
}

function scheduleProductDetailHeightSync(productInfo) {
  if (productInfo instanceof HTMLElement) resetProductDetailHeight(productInfo);
}

function bindProductDetailHeightSync(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpDetailHeightSyncBound === 'true') return;

  productInfo.dataset.hpDetailHeightSyncBound = 'true';
  resetProductDetailHeight(productInfo);
}

function bindProductCartPressFeedback(productInfo) {
  const button = productInfo.querySelector('.product-form-buttons .hp-product-cart-action');
  if (!(button instanceof HTMLButtonElement) || button.dataset.hpCartPressFeedbackBound === 'true') return;

  button.dataset.hpCartPressFeedbackBound = 'true';
  const pressedClass = 'hp-product-cart-action--pressed';
  const minimumPressDuration = 150;
  let pressedAt = 0;
  let releaseTimer = 0;

  const release = (immediate = false) => {
    window.clearTimeout(releaseTimer);

    if (immediate) {
      button.classList.remove(pressedClass);
      return;
    }

    const remainingDuration = Math.max(0, minimumPressDuration - (window.performance.now() - pressedAt));
    releaseTimer = window.setTimeout(() => button.classList.remove(pressedClass), remainingDuration);
  };

  button.addEventListener('pointerdown', (event) => {
    if (button.disabled || (event.pointerType === 'mouse' && event.button !== 0)) return;

    window.clearTimeout(releaseTimer);
    pressedAt = window.performance.now();
    button.classList.add(pressedClass);
  });
  button.addEventListener('pointerup', () => release());
  button.addEventListener('pointercancel', () => release(true));
  button.addEventListener('dragstart', () => release(true));
  button.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'mouse') release(true);
  });
}

function bindProductAddonVariantSelectors(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;

  scope.querySelectorAll('[data-hp-addon-variant-select]').forEach((select) => {
    if (!(select instanceof HTMLSelectElement) || select.dataset.hpAddonVariantReady === 'true') return;

    select.dataset.hpAddonVariantReady = 'true';
    select.addEventListener('change', () => {
      const selectedOption = select.options[select.selectedIndex];
      const form = select.closest('form');
      const formComponent = select.closest('product-form-component');
      const item = select.closest('[data-hp-product-addon-item]');
      const variantInput = form?.querySelector('[data-hp-addon-variant-input]');
      const button = form?.querySelector('[data-hp-addon-submit]');
      const price = item?.querySelector('[data-hp-addon-price]');
      const memberPrice = price?.querySelector('[data-hp-member-price]');
      const addToCartContainer = form?.querySelector('add-to-cart-component');

      if (variantInput instanceof HTMLInputElement) variantInput.value = selectedOption?.value || '';
      if (formComponent instanceof HTMLElement) {
        formComponent.dataset.variantPrice = selectedOption?.dataset.priceCents || '';
        formComponent.dataset.variantAvailable = selectedOption?.dataset.available || 'false';
        formComponent.dataset.cartQuantity = selectedOption?.dataset.cartQuantity || '0';
        formComponent.dataset.variantMaxQuantity = selectedOption?.dataset.maxQuantity || '';
      }
      if (memberPrice instanceof HTMLElement && selectedOption?.dataset.price && selectedOption?.dataset.memberPrice) {
        const storePrice = memberPrice.querySelector('.hp-member-price__store');
        const value = memberPrice.querySelector('.hp-member-price__value');
        if (storePrice instanceof HTMLElement) storePrice.textContent = selectedOption.dataset.price;
        if (value instanceof HTMLElement) value.textContent = selectedOption.dataset.memberPrice;
        memberPrice.dataset.memberStorePrice = selectedOption.dataset.priceCents || '';
      } else if (price instanceof HTMLElement && selectedOption?.dataset.price) {
        price.textContent = selectedOption.dataset.price;
      }
      if (button instanceof HTMLButtonElement) {
        button.disabled = selectedOption?.dataset.available !== 'true';
      }
      if (addToCartContainer instanceof HTMLElement && selectedOption?.dataset.variantMedia) {
        addToCartContainer.dataset.productVariantMedia = selectedOption.dataset.variantMedia;
      }
      formComponent?.syncStandaloneAvailability?.();
    });

    select.closest('product-form-component')?.syncStandaloneAvailability?.();
  });
}

function syncProductAddonTitleRows(addon) {
  if (!(addon instanceof HTMLElement) || !addon.isConnected) return;

  const titles = Array.from(addon.querySelectorAll('.hp-product-addon__title'));
  let maximumLines = 1;

  titles.forEach((title) => {
    if (!(title instanceof HTMLElement) || title.clientWidth <= 0) return;

    const measurement = title.cloneNode(true);
    if (!(measurement instanceof HTMLElement)) return;

    measurement.setAttribute('aria-hidden', 'true');
    measurement.style.setProperty('--hp-product-addon-title-lines', '20');
    measurement.style.position = 'absolute';
    measurement.style.left = '-10000px';
    measurement.style.top = '0';
    measurement.style.width = `${title.clientWidth}px`;
    measurement.style.height = 'auto';
    measurement.style.maxHeight = 'none';
    measurement.style.overflow = 'visible';
    measurement.style.visibility = 'hidden';
    measurement.style.pointerEvents = 'none';
    document.body.append(measurement);

    const lineHeight = Number.parseFloat(getComputedStyle(measurement).lineHeight) || 1;
    const naturalLines = Math.max(1, Math.ceil((measurement.scrollHeight - 0.5) / lineHeight));
    maximumLines = Math.max(maximumLines, Math.min(3, naturalLines));
    measurement.remove();
  });

  addon.style.setProperty('--hp-product-addon-title-lines', String(maximumLines));
}

function syncAllProductAddonTitleRows() {
  productAddonTitleResizeFrame = 0;
  productAddonTitleCollections.forEach((addon) => {
    if (!addon.isConnected) {
      productAddonTitleCollections.delete(addon);
      return;
    }
    syncProductAddonTitleRows(addon);
  });
}

function scheduleProductAddonTitleRows() {
  if (productAddonTitleResizeFrame) return;
  productAddonTitleResizeFrame = window.requestAnimationFrame(syncAllProductAddonTitleRows);
}

function applyProductInfoOverlayBackgroundInert(overlay) {
  productInfoOverlayState.inertedElements = [];
  let branch = overlay;

  while (
    branch instanceof HTMLElement
    && branch !== document.body
    && branch.parentElement
  ) {
    Array.from(branch.parentElement.children).forEach((sibling) => {
      if (
        sibling === branch
        || !(sibling instanceof HTMLElement)
        || sibling.hasAttribute('inert')
      ) {
        return;
      }
      sibling.setAttribute('inert', '');
      productInfoOverlayState.inertedElements.push(sibling);
    });
    branch = branch.parentElement;
  }
}

function releaseProductInfoOverlayBackgroundInert() {
  productInfoOverlayState.inertedElements.forEach((element) => {
    if (element.isConnected) element.removeAttribute('inert');
  });
  productInfoOverlayState.inertedElements = [];
}

function captureProductInfoOverlayTouchStart(event) {
  const touch = event.touches?.[0];
  productInfoOverlayState.touchX = touch?.clientX || 0;
  productInfoOverlayState.touchY = touch?.clientY || 0;
}

function blockProductInfoOverlayBackgroundScroll(event) {
  const overlay = productInfoOverlayState.modal;
  if (!(overlay instanceof HTMLElement) || !overlay.classList.contains('is-open')) return;

  const target = event.target instanceof Element ? event.target : null;
  const scrollable = target?.closest?.('.hp-tier-info__panel, .hp-product-guide__fit-info-card');
  if (!(scrollable instanceof HTMLElement) || !overlay.contains(scrollable)) {
    event.preventDefault();
    return;
  }

  const touch = event.touches?.[0];
  const deltaY = touch
    ? productInfoOverlayState.touchY - touch.clientY
    : Number(event.deltaY || 0);
  if (touch) {
    productInfoOverlayState.touchX = touch.clientX;
    productInfoOverlayState.touchY = touch.clientY;
  }

  const canScroll = scrollable.scrollHeight > scrollable.clientHeight + 1;
  const atTop = scrollable.scrollTop <= 1;
  const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;
  if (!canScroll || (deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) {
    event.preventDefault();
  }
}

// Keep the body in document flow so sticky headers retain their viewport position.
function applyFlowPreservingModalScrollLock(state) {
  state.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
  state.previousDocumentScrollBehavior = document.documentElement.style.scrollBehavior;
  state.previousDocumentOverflow = document.documentElement.style.overflow;
  state.previousDocumentOverscrollBehavior = document.documentElement.style.overscrollBehavior;
  state.previousDocumentScrollbarGutter = document.documentElement.style.scrollbarGutter;
  state.previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;
  state.previousBodyPaddingRight = document.body.style.paddingRight;

  const scrollbarWidth = getModalScrollbarCompensation();
  const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
  const supportsStableScrollbarGutter = window.CSS?.supports?.('scrollbar-gutter: stable') === true;

  document.documentElement.style.scrollBehavior = 'auto';
  document.documentElement.style.scrollbarGutter = 'stable';
  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.overscrollBehavior = 'none';
  document.body.style.overscrollBehavior = 'none';
  if (scrollbarWidth > 0 && !supportsStableScrollbarGutter) {
    document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
  }
}

function applyProductInfoOverlayScrollLock() {
  applyFlowPreservingModalScrollLock(productInfoOverlayState);
  document.addEventListener('touchstart', captureProductInfoOverlayTouchStart, {
    passive: true,
    capture: true,
  });
  document.addEventListener('touchmove', blockProductInfoOverlayBackgroundScroll, {
    passive: false,
    capture: true,
  });
  document.addEventListener('wheel', blockProductInfoOverlayBackgroundScroll, {
    passive: false,
    capture: true,
  });
}

function releaseProductInfoOverlayScrollLock() {
  document.removeEventListener('touchstart', captureProductInfoOverlayTouchStart, true);
  document.removeEventListener('touchmove', blockProductInfoOverlayBackgroundScroll, true);
  document.removeEventListener('wheel', blockProductInfoOverlayBackgroundScroll, true);
  releaseFlowPreservingModalScrollLock(productInfoOverlayState);
}

function releaseFlowPreservingModalScrollLock(state) {
  document.documentElement.style.overflow = state.previousDocumentOverflow || '';
  document.documentElement.style.overscrollBehavior = state.previousDocumentOverscrollBehavior || '';
  document.documentElement.style.scrollbarGutter = state.previousDocumentScrollbarGutter || '';
  document.body.style.overscrollBehavior = state.previousBodyOverscrollBehavior || '';
  document.body.style.paddingRight = state.previousBodyPaddingRight || '';

  const currentScrollY = window.scrollY || document.documentElement.scrollTop || 0;
  if (Math.abs(currentScrollY - state.scrollY) > 1) {
    window.scrollTo(0, state.scrollY);
  }
  document.documentElement.style.scrollBehavior = state.previousDocumentScrollBehavior || '';
}

function trapProductInfoOverlayFocus(event, overlay) {
  const focusable = Array.from(
    overlay.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => (
    element instanceof HTMLElement
    && !element.hasAttribute('hidden')
    && element.getAttribute('aria-hidden') !== 'true'
    && element.getClientRects().length > 0
  ));
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!focusable.includes(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus({ preventScroll: true });
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function handleProductInfoOverlayKeydown(event) {
  const overlay = productInfoOverlayState.modal;
  const controller = productInfoOverlayState.controller;
  if (!(overlay instanceof HTMLElement) || !controller) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    controller.close({ restoreFocus: true });
  } else if (event.key === 'Tab') {
    trapProductInfoOverlayFocus(event, overlay);
  }
}

function bindProductInfoOverlayConnectionObserver() {
  if (productInfoOverlayConnectionObserver || !document.documentElement) return;

  productInfoOverlayConnectionObserver = new MutationObserver(() => {
    const activeOverlay = productInfoOverlayState.modal;
    const controller = productInfoOverlayState.controller;
    if (
      activeOverlay instanceof HTMLElement
      && activeOverlay.isConnected
      && controller?.trigger?.isConnected
      && controller?.productInfo?.isConnected
    ) {
      return;
    }

    if (controller) controller.close({ restoreFocus: false, immediate: true });
    else if (activeOverlay instanceof HTMLElement) releaseProductInfoOverlay(activeOverlay);

    if (activeOverlay instanceof HTMLElement) {
      activeOverlay.remove();
      productInfoOverlayElements.delete(activeOverlay);
    }
  });
  productInfoOverlayConnectionObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function openProductInfoOverlay(overlay, controller) {
  if (
    !(overlay instanceof HTMLElement)
    || overlay.classList.contains('is-open')
    || !controller
  ) {
    return false;
  }

  const activeOverlay = productInfoOverlayState.modal;
  const activeController = productInfoOverlayState.controller;
  if (activeOverlay instanceof HTMLElement && activeOverlay !== overlay) {
    if (activeController) activeController.close({ restoreFocus: false, immediate: true });
    else releaseProductInfoOverlay(activeOverlay);
  }

  closeRecommendationQuickView();
  closeProductImageModal();
  closeProductHelpDrawer();
  productInfoOverlayState.modal = overlay;
  productInfoOverlayState.controller = controller;
  document.documentElement.classList.add('hp-product-info-overlay-open');
  applyProductInfoOverlayScrollLock();
  overlay.removeAttribute('inert');
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('is-open');
  applyProductInfoOverlayBackgroundInert(overlay);
  document.addEventListener('keydown', handleProductInfoOverlayKeydown);
  bindProductInfoOverlayConnectionObserver();
  return true;
}

function releaseProductInfoOverlay(overlay) {
  if (productInfoOverlayState.modal !== overlay) return;
  productInfoOverlayState.modal = null;
  productInfoOverlayState.controller = null;
  productInfoOverlayConnectionObserver?.disconnect();
  productInfoOverlayConnectionObserver = null;
  document.removeEventListener('keydown', handleProductInfoOverlayKeydown);
  releaseProductInfoOverlayBackgroundInert();
  document.documentElement.classList.remove('hp-product-info-overlay-open');
  releaseProductInfoOverlayScrollLock();
}

function resetProductInfoOverlays() {
  document
    .querySelectorAll('.hp-tier-info__popover.is-open, .hp-product-guide__fit-info-panel.is-open')
    .forEach((overlay) => {
      const controller = productInfoOverlayControllers.get(overlay);
      if (controller) controller.close({ restoreFocus: false, immediate: true });
    });

  document.querySelectorAll('[data-hp-tier-info-trigger], [data-hp-product-sizing-info-trigger]').forEach((trigger) => {
    trigger.setAttribute('aria-expanded', 'false');
  });
  document.querySelectorAll('[data-hp-tier-info]').forEach((info) => {
    delete info.dataset.open;
  });

  if (productInfoOverlayState.modal instanceof HTMLElement) {
    releaseProductInfoOverlay(productInfoOverlayState.modal);
  }
}

function bindProductAddonTitleRows(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  const addons = [];

  if (scope instanceof HTMLElement && scope.matches('[data-hp-product-addon]')) addons.push(scope);
  addons.push(...scope.querySelectorAll('[data-hp-product-addon]'));

  addons.forEach((addon) => {
    if (!(addon instanceof HTMLElement)) return;
    productAddonTitleCollections.add(addon);
    syncProductAddonTitleRows(addon);
  });

  if (!productAddonTitleResizeBound) {
    productAddonTitleResizeBound = true;
    window.addEventListener('resize', scheduleProductAddonTitleRows, { passive: true });
    document.fonts?.ready?.then(scheduleProductAddonTitleRows).catch(() => undefined);
  }
}

function scheduleProductInfoOverlayReveal(overlay, surface, focusTarget, onReveal = null) {
  overlay.classList.remove('is-visible');
  surface.style.removeProperty('--hp-product-popup-locked-height');
  let firstFrame = 0;
  let secondFrame = 0;
  let cancelled = false;

  firstFrame = window.requestAnimationFrame(() => {
    firstFrame = 0;
    secondFrame = window.requestAnimationFrame(() => {
      secondFrame = 0;
      if (cancelled || !overlay.classList.contains('is-open')) return;

      const surfaceHeight = Math.ceil(surface.getBoundingClientRect().height);
      if (surfaceHeight > 0) {
        surface.style.setProperty('--hp-product-popup-locked-height', `${surfaceHeight}px`);
      }
      overlay.classList.add('is-visible');
      onReveal?.();
      focusTarget?.focus?.({ preventScroll: true });
    });
  });

  return () => {
    cancelled = true;
    if (firstFrame) window.cancelAnimationFrame(firstFrame);
    if (secondFrame) window.cancelAnimationFrame(secondFrame);
    firstFrame = 0;
    secondFrame = 0;
  };
}

function createProductInfoOverlayActivationTracker(element) {
  let keyboardActivation = false;
  const markPointerActivation = () => {
    keyboardActivation = false;
  };

  element.addEventListener('pointerdown', markPointerActivation, { passive: true });
  element.addEventListener('touchstart', markPointerActivation, { passive: true });
  element.addEventListener('mousedown', markPointerActivation, { passive: true });
  element.addEventListener('keydown', (event) => {
    keyboardActivation = event.key === 'Enter' || event.key === ' ';
  });

  return () => {
    const wasKeyboardActivation = keyboardActivation;
    keyboardActivation = false;
    return wasKeyboardActivation;
  };
}

function portalProductInfoOverlay(overlay) {
  if (overlay.parentElement === document.body) return;
  overlay.dataset.hpProductOverlayPortaled = 'true';
  document.body.append(overlay);
  productInfoOverlayElements.add(overlay);
}

function cleanupDisconnectedProductInfoOverlays() {
  productInfoOverlayElements.forEach((overlay) => {
    const controller = productInfoOverlayControllers.get(overlay);
    if (
      overlay.isConnected
      && controller?.trigger?.isConnected
      && controller?.productInfo?.isConnected
    ) {
      return;
    }
    if (productInfoOverlayState.modal === overlay && controller) {
      controller.close({ restoreFocus: false, immediate: true });
    }
    overlay.remove();
    productInfoOverlayElements.delete(overlay);
  });
}

function bindMemberTierInfo(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;

  scope.querySelectorAll('[data-hp-tier-info]').forEach((info) => {
    if (!(info instanceof HTMLElement) || info.dataset.hpTierInfoReady === 'true') return;

    const trigger = info.querySelector('[data-hp-tier-info-trigger]');
    const popover = info.querySelector('[data-hp-tier-info-popover]');
    const closeButtons = info.querySelectorAll('[data-hp-tier-info-close]');
    const closeButton = info.querySelector('.hp-tier-info__close');
    const panelShell = popover?.querySelector('.hp-tier-info__panel-shell');
    const panel = popover?.querySelector('.hp-tier-info__panel');
    if (
      !(trigger instanceof HTMLButtonElement) ||
      !(popover instanceof HTMLElement) ||
      !(panelShell instanceof HTMLElement) ||
      !(panel instanceof HTMLElement)
    ) {
      return;
    }

    info.dataset.hpTierInfoReady = 'true';
    let cancelReveal = () => {};
    const consumeTriggerKeyboardActivation = createProductInfoOverlayActivationTracker(trigger);
    const controller = {
      productInfo: info.closest(PRODUCT_PAGE_SELECTOR),
      trigger,
      close: null,
    };

    const close = ({ restoreFocus = true } = {}) => {
      const wasOpen = popover.classList.contains('is-open');
      if (!wasOpen && productInfoOverlayState.modal !== popover) return;

      cancelReveal();
      cancelReveal = () => {};
      popover.classList.remove('is-visible', 'is-open');
      popover.setAttribute('aria-hidden', 'true');
      popover.setAttribute('inert', '');
      trigger.setAttribute('aria-expanded', 'false');
      delete info.dataset.open;

      panel.style.removeProperty('--hp-product-popup-locked-height');
      releaseProductInfoOverlay(popover);
      if (!wasOpen || document.visibilityState === 'hidden' || !trigger.isConnected) return;
      if (restoreFocus) {
        trigger.focus({ preventScroll: true });
      } else {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        trigger.blur();
      }
    };
    controller.close = close;
    productInfoOverlayControllers.set(popover, controller);
    portalProductInfoOverlay(popover);

    const open = ({ focusClose = false } = {}) => {
      if (popover.classList.contains('is-open')) return;
      if (!openProductInfoOverlay(popover, controller)) return;
      trigger.setAttribute('aria-expanded', 'true');
      info.dataset.open = 'true';
      cancelReveal = scheduleProductInfoOverlayReveal(
        popover,
        panel,
        focusClose && closeButton instanceof HTMLElement ? closeButton : panel
      );
    };

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open({ focusClose: consumeTriggerKeyboardActivation() });
    });

    closeButtons.forEach((button) => {
      const consumeCloseKeyboardActivation = createProductInfoOverlayActivationTracker(button);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        close({ restoreFocus: consumeCloseKeyboardActivation() });
      });
    });
  });
}

function bindProductSizingInfo(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;

  scope.querySelectorAll('[data-hp-product-sizing-info-panel]').forEach((overlay) => {
    if (!(overlay instanceof HTMLElement) || overlay.dataset.hpProductSizingInfoReady === 'true') return;

    const wrapper = overlay.closest('.hp-product-guide__shoe-fit-with-info');
    const trigger = wrapper?.querySelector('[data-hp-product-sizing-info-trigger]');
    const closeButtons = overlay.querySelectorAll('[data-hp-product-sizing-info-close]');
    const closeButton = overlay.querySelector('.hp-product-guide__fit-info-close');
    const card = overlay.querySelector('.hp-product-guide__fit-info-card');
    if (
      !(trigger instanceof HTMLButtonElement)
      || !(closeButton instanceof HTMLButtonElement)
      || !(card instanceof HTMLElement)
    ) {
      return;
    }

    overlay.dataset.hpProductSizingInfoReady = 'true';
    let cancelReveal = () => {};
    const consumeTriggerKeyboardActivation = createProductInfoOverlayActivationTracker(trigger);
    const controller = {
      productInfo: overlay.closest(PRODUCT_PAGE_SELECTOR),
      trigger,
      close: null,
    };

    const close = ({ restoreFocus = true } = {}) => {
      const wasOpen = overlay.classList.contains('is-open');
      if (!wasOpen && productInfoOverlayState.modal !== overlay) return;

      cancelReveal();
      cancelReveal = () => {};
      overlay.classList.remove('is-visible', 'is-open');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('inert', '');
      trigger.setAttribute('aria-expanded', 'false');

      card.style.removeProperty('--hp-product-popup-locked-height');
      releaseProductInfoOverlay(overlay);
      if (!wasOpen || document.visibilityState === 'hidden' || !trigger.isConnected) return;
      if (restoreFocus) {
        trigger.focus({ preventScroll: true });
      } else {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        trigger.blur();
      }
    };
    controller.close = close;
    productInfoOverlayControllers.set(overlay, controller);
    portalProductInfoOverlay(overlay);

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (!overlay.classList.contains('is-open')) {
        if (!openProductInfoOverlay(overlay, controller)) return;
        trigger.setAttribute('aria-expanded', 'true');
        const focusClose = consumeTriggerKeyboardActivation();
        cancelReveal = scheduleProductInfoOverlayReveal(
          overlay,
          card,
          focusClose ? closeButton : card
        );
      }
    });

    closeButtons.forEach((button) => {
      const consumeCloseKeyboardActivation = createProductInfoOverlayActivationTracker(button);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        close({ restoreFocus: consumeCloseKeyboardActivation() });
      });
    });
  });
}

function bindProductTierVariantRefresh(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpTierVariantRefreshReady === 'true') return;

  productInfo.dataset.hpTierVariantRefreshReady = 'true';
  productInfo.addEventListener(
    'variant:update',
    () => {
      const activeController = productInfoOverlayState.controller;
      if (activeController?.productInfo === productInfo) {
        activeController.close({ restoreFocus: false, immediate: true });
      }
      window.queueMicrotask(() => {
        cleanupDisconnectedProductInfoOverlays();
        bindMemberTierInfo(productInfo);
        bindProductSizingInfo(productInfo);
      });
    },
    true
  );
}

function enhanceRecommendationQuickViewButtons(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  scope.querySelectorAll(`${PRODUCT_RECOMMENDATIONS_SELECTOR} product-card`).forEach((card) => {
    if (!(card instanceof HTMLElement) || card.dataset.hpRecommendationQuickReady === 'true') return;

    const gallery = card.querySelector('.card-gallery');
    const productUrl = getRecommendationProductUrl(card);
    if (!(gallery instanceof HTMLElement) || !productUrl) return;

    card.dataset.hpRecommendationQuickReady = 'true';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hp-product-card__quick hp-product-recommendation__quick';
    button.setAttribute('data-hp-recommendation-open', '');
    button.setAttribute('aria-label', 'Open product popup');
    button.innerHTML = `
      <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="6.25"></circle>
        <path d="m16 16 4.25 4.25"></path>
      </svg>
    `;
    button.addEventListener('pointerenter', () => primeRecommendationQuickViewProduct(card), { passive: true });
    button.addEventListener('focus', () => primeRecommendationQuickViewProduct(card));
    gallery.append(button);
  });
  scheduleRecommendationQuickViewPrefetch(scope);
}

function bindRecommendationQuickView() {
  if (recommendationQuickViewBound) return;

  recommendationQuickViewBound = true;
  document.addEventListener('click', handleRecommendationQuickViewClick);

  if (!productRecommendationsObserver && document.body) {
    productRecommendationsObserver = new MutationObserver(() => enhanceRecommendationQuickViewButtons(document));
    productRecommendationsObserver.observe(document.body, { childList: true, subtree: true });
  }
}

function scheduleRecommendationQuickViewPrefetch(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
  const sections = scope.matches?.(PRODUCT_RECOMMENDATIONS_SELECTOR)
    ? [scope]
    : Array.from(scope.querySelectorAll(PRODUCT_RECOMMENDATIONS_SELECTOR));

  sections.forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.hpRecommendationPrefetchReady === 'true') return;

    section.dataset.hpRecommendationPrefetchReady = 'true';
    if (typeof IntersectionObserver === 'function') {
      recommendationPrefetchObserver ||= new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            recommendationPrefetchObserver?.unobserve(entry.target);
            prefetchRecommendationQuickViewProducts(entry.target);
          });
        },
        { rootMargin: '700px 0px' }
      );
      recommendationPrefetchObserver.observe(section);
      return;
    }

    prefetchRecommendationQuickViewProducts(section);
  });
}

function prefetchRecommendationQuickViewProducts(section) {
  requestProductIdleCallback(() => {
    Array.from(section.querySelectorAll('product-card')).slice(0, 5).forEach((card) => {
      if (card instanceof HTMLElement) primeRecommendationQuickViewProduct(card);
    });
  });
}

function primeRecommendationQuickViewProduct(card) {
  const productPath = getRecommendationProductUrl(card);
  if (!productPath) return;

  loadRecommendationProduct(productPath)
    .then((product) => {
      const firstImage = getRecommendationQuickViewImages(product)[0];
      preloadImageSrc(firstImage?.previewSrc || firstImage?.src || '');
    })
    .catch(() => {});
}

async function handleRecommendationQuickViewClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const modal = recommendationQuickViewState.modal;
  const close = target.closest('[data-hp-recommendation-close]');
  if (close) {
    event.preventDefault();
    closeRecommendationQuickView();
    return;
  }

  const panelToggle = target.closest('[data-hp-recommendation-panel-toggle]');
  if (panelToggle) {
    event.preventDefault();
    setRecommendationQuickViewPanel(panelToggle.getAttribute('data-panel') || 'images');
    return;
  }

  const prev = target.closest('[data-hp-recommendation-prev]');
  if (prev) {
    event.preventDefault();
    showRecommendationQuickViewImage(recommendationQuickViewState.activeImageIndex - 1);
    return;
  }

  const next = target.closest('[data-hp-recommendation-next]');
  if (next) {
    event.preventDefault();
    showRecommendationQuickViewImage(recommendationQuickViewState.activeImageIndex + 1);
    return;
  }

  const dot = target.closest('[data-hp-recommendation-dot]');
  if (dot) {
    event.preventDefault();
    showRecommendationQuickViewImage(Number(dot.getAttribute('data-index') || 0));
    return;
  }

  if (modal?.classList.contains('is-open') && !target.closest('.hp-product-modal__content')) {
    event.preventDefault();
    closeRecommendationQuickView();
    return;
  }

  const opener = target.closest('[data-hp-recommendation-open]');
  if (!opener) return;

  const card = opener.closest('product-card');
  if (!(card instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();
  await openRecommendationQuickView(card, opener);
}

function getRecommendationProductUrl(card) {
  const link = card?.querySelector('.product-card__link[href], a[href*="/products/"]');
  if (!(link instanceof HTMLAnchorElement)) return '';

  const url = new URL(link.getAttribute('href') || '', window.location.origin);
  url.search = '';
  url.hash = '';
  return url.pathname;
}

async function openRecommendationQuickView(card, opener) {
  const productPath = getRecommendationProductUrl(card);
  if (!productPath) return;

  const requestId = ++recommendationQuickViewState.requestId;
  const button = opener instanceof HTMLElement ? opener : null;
  if (button) button.dataset.hpRecommendationRequestId = String(requestId);
  button?.setAttribute('aria-busy', 'true');

  try {
    const product = await loadRecommendationProduct(productPath);
    if (
      requestId !== recommendationQuickViewState.requestId ||
      document.querySelector('dialog:modal')
    ) {
      return;
    }
    renderRecommendationQuickView(product);
    recommendationQuickViewState.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    recommendationQuickViewState.activeProduct = product;
    recommendationQuickViewState.activeImageIndex = 0;
    setRecommendationQuickViewPanel('images');
    lockRecommendationQuickViewScroll();

    const modal = ensureRecommendationQuickViewModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    showRecommendationQuickViewImage(0, { instant: true });
    modal.querySelector('.hp-product-modal__content')?.focus?.({ preventScroll: true });
  } catch (error) {
    if (requestId !== recommendationQuickViewState.requestId) return;
    console.error('Product popup could not open:', error);
    window.location.href = productPath;
  } finally {
    if (button?.dataset.hpRecommendationRequestId === String(requestId)) {
      delete button.dataset.hpRecommendationRequestId;
      button.removeAttribute('aria-busy');
    }
  }
}

async function loadRecommendationProduct(productPath) {
  if (productQuickViewCache.has(productPath)) return productQuickViewCache.get(productPath);

  const request = fetch(`${productPath}.js`, { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const rawProduct = await response.json();
      return mapShopifyProductForQuickView(rawProduct, productPath);
    })
    .catch((error) => {
      productQuickViewCache.delete(productPath);
      throw error;
    });

  productQuickViewCache.set(productPath, request);
  return request;
}

function mapShopifyProductForQuickView(product, productPath) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const prices = variants.map((variant) => Number(variant.price || 0)).filter((price) => Number.isFinite(price) && price > 0);
  const fallbackPrice = Number(product?.price || 0);
  const priceMin = Number(product?.price_min || Math.min(...prices, fallbackPrice) || fallbackPrice || 0);
  const priceMax = Number(product?.price_max || Math.max(...prices, fallbackPrice) || priceMin || 0);
  const rawImages = Array.isArray(product?.images) ? product.images : [];
  const featuredImage = normalizeRecommendationImageSrc(product?.featured_image || rawImages[0] || '');

  return {
    id: product?.id,
    handle: product?.handle || productPath.split('/').filter(Boolean).pop(),
    url: productPath,
    title: product?.title || '',
    vendor: product?.vendor || '',
    type: product?.type || '',
    priceMin,
    priceMax,
    featuredImage: getSizedShopifyImageSrc(featuredImage, RECOMMENDATION_IMAGE_FULL_WIDTH),
    images: rawImages.slice(0, 8).map((image) => {
      const normalizedImage = normalizeRecommendationImageSrc(image);
      return {
        src: getSizedShopifyImageSrc(normalizedImage, RECOMMENDATION_IMAGE_FULL_WIDTH),
        previewSrc: getSizedShopifyImageSrc(normalizedImage, RECOMMENDATION_IMAGE_PREVIEW_WIDTH),
        alt: product?.title || '',
      };
    }).filter((image) => image.src),
    descriptionHtml: product?.description || '<p>No description available.</p>',
  };
}

function normalizeRecommendationImageSrc(image) {
  const src = typeof image === 'string' ? image : image?.src || image?.url || '';
  if (!src) return '';
  if (src.startsWith('//')) return `${window.location.protocol}${src}`;
  return src;
}

function ensureRecommendationQuickViewModal() {
  if (recommendationQuickViewState.modal instanceof HTMLElement) return recommendationQuickViewState.modal;

  const modal = document.createElement('div');
  modal.className = 'hp-product-modal hp-product-modal--recommendations';
  modal.setAttribute('data-hp-modal-view', 'images');
  modal.setAttribute('data-hp-recommendation-modal', '');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <button type="button" class="hp-product-modal__backdrop" data-hp-recommendation-close aria-label="Close product details"></button>
    <div class="hp-product-modal__content" role="dialog" tabindex="-1" aria-modal="true" aria-labelledby="hp-recommendation-modal-title">
      <button type="button" class="hp-product-modal__close" data-hp-recommendation-close aria-label="Close product details">&times;</button>
      <div class="hp-product-modal__switch" role="tablist" aria-label="Product popup view">
        <button type="button" class="hp-product-modal__switch-button is-active" data-hp-recommendation-panel-toggle data-panel="images" role="tab" aria-selected="true">Images</button>
        <button type="button" class="hp-product-modal__switch-button" data-hp-recommendation-panel-toggle data-panel="details" role="tab" aria-selected="false">Details</button>
      </div>
      <div class="hp-product-modal__layout">
        <section class="hp-product-modal__gallery" data-hp-modal-panel="images" aria-label="Product images">
          <div class="hp-product-modal__media">
            <button type="button" class="hp-product-modal__nav hp-product-modal__nav--prev" data-hp-recommendation-prev aria-label="Previous product image" hidden>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>
            </button>
            <div class="hp-product-popup-track" data-hp-popup-track tabindex="-1"></div>
            <button type="button" class="hp-product-modal__nav hp-product-modal__nav--next" data-hp-recommendation-next aria-label="Next product image" hidden>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>
            </button>
          </div>
          <div class="hp-product-modal__dots" data-hp-recommendation-dots hidden></div>
        </section>
        <section class="hp-product-modal__details" data-hp-modal-panel="details" aria-label="Product details">
          <p class="hp-product-modal__kicker" data-hp-recommendation-vendor></p>
          <h2 class="hp-product-modal__title" id="hp-recommendation-modal-title" data-hp-recommendation-title></h2>
          <p class="hp-product-modal__price" data-hp-recommendation-price></p>
          <div class="hp-product-modal__description rte" data-hp-recommendation-description></div>
          <a href="#" class="hp-product-modal__link" data-hp-recommendation-link>View product page</a>
        </section>
      </div>
    </div>
  `;
  document.body.append(modal);
  recommendationQuickViewState.modal = modal;
  bindRecommendationQuickViewSwipe(modal);
  return modal;
}

function renderRecommendationQuickView(product) {
  const modal = ensureRecommendationQuickViewModal();
  const images = getRecommendationQuickViewImages(product);
  resetProductPopupGallery(recommendationQuickViewState);
  renderProductPopupGallery(modal, images);

  modal.querySelector('[data-hp-recommendation-title]').textContent = product.title || '';
  modal.querySelector('[data-hp-recommendation-vendor]').textContent = [product.vendor, product.type].filter(Boolean).join(' / ');
  modal.querySelector('[data-hp-recommendation-price]').textContent = formatRecommendationProductPrice(product);
  modal.querySelector('[data-hp-recommendation-description]').innerHTML = product.descriptionHtml || '<p>No description available.</p>';
  modal.querySelector('[data-hp-recommendation-link]').href = product.url || '#';

  const dots = modal.querySelector('[data-hp-recommendation-dots]');
  if (dots instanceof HTMLElement) {
    dots.hidden = images.length <= 1;
    dots.innerHTML = images.map((_, index) => (
      `<button type="button" class="hp-product-modal__dot${index === recommendationQuickViewState.activeImageIndex ? ' is-active' : ''}" data-hp-recommendation-dot data-index="${index}" aria-label="Show image ${index + 1}"></button>`
    )).join('');
  }
}

function setRecommendationQuickViewPanel(panel = 'images') {
  const modal = ensureRecommendationQuickViewModal();
  const nextPanel = panel === 'details' ? 'details' : 'images';
  modal.setAttribute('data-hp-modal-view', nextPanel);
  modal.querySelectorAll('[data-hp-recommendation-panel-toggle]').forEach((button) => {
    const isActive = button.getAttribute('data-panel') === nextPanel;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  modal.querySelectorAll('.hp-product-modal__content, .hp-product-modal__details, .hp-product-modal__description').forEach((element) => {
    if (element instanceof HTMLElement) element.scrollTop = 0;
  });
}

function getRecommendationQuickViewImages(product) {
  const images = Array.isArray(product?.images) ? product.images.filter((image) => image.src || image.previewSrc) : [];
  if (images.length) return images;
  if (product?.featuredImage) return [{ src: product.featuredImage, previewSrc: product.featuredImage, alt: product.title }];
  return [];
}

function showRecommendationQuickViewImage(index, options = {}) {
  const modal = ensureRecommendationQuickViewModal();
  const product = recommendationQuickViewState.activeProduct;
  const images = getRecommendationQuickViewImages(product);
  const nextIndex = Math.max(0, Math.min(images.length - 1, index));
  const image = getProductPopupSlideImage(modal, nextIndex);
  const prev = modal.querySelector('[data-hp-recommendation-prev]');
  const next = modal.querySelector('[data-hp-recommendation-next]');

  if (!(image instanceof HTMLImageElement)) return;
  if (!images.length) {
    image.hidden = true;
    image.removeAttribute('src');
    return;
  }

  const nextImage = images[nextIndex];
  recommendationQuickViewState.activeImageIndex = nextIndex;

  setModalImageSource(image, { ...nextImage, alt: nextImage.alt || product?.title || '' });
  preloadNeighborImages(images, nextIndex);
  updateProductPopupSlides(modal, nextIndex, 'data-hp-recommendation-image');
  if (options.scroll !== false) scrollProductPopupGallery(recommendationQuickViewState, nextIndex, options);

  if (prev instanceof HTMLButtonElement) {
    prev.hidden = images.length <= 1;
    prev.disabled = nextIndex <= 0;
    prev.classList.toggle('is-muted', nextIndex <= 0);
  }
  if (next instanceof HTMLButtonElement) {
    next.hidden = images.length <= 1;
    next.disabled = nextIndex >= images.length - 1;
    next.classList.toggle('is-muted', nextIndex >= images.length - 1);
  }
  modal.querySelectorAll('[data-hp-recommendation-dot]').forEach((dot) => {
    dot.classList.toggle('is-active', Number(dot.getAttribute('data-index')) === nextIndex);
  });
}

function renderProductPopupGallery(modal, images) {
  const track = modal.querySelector('[data-hp-popup-track]');
  if (!track) return;
  const slides = images.map((data) => {
    const slide = document.createElement('div');
    slide.className = 'hp-product-popup-slide';
    const image = document.createElement('img');
    image.className = 'hp-product-modal__image';
    image.width = 1600;
    image.height = 1600;
    image.alt = data.alt || '';
    image.draggable = false;
    image.loading = 'eager';
    image.decoding = 'async';
    image.src = data.previewSrc || data.src;
    slide.append(image);
    return slide;
  });
  track.replaceChildren(...slides);
}

function getProductPopupSlideImage(modal, index) {
  return modal.querySelector('[data-hp-popup-track]')?.children[index]?.querySelector('img');
}

function updateProductPopupSlides(modal, index, imageAttribute) {
  const track = modal.querySelector('[data-hp-popup-track]');
  if (!track) return;
  Array.from(track.children).forEach((slide, slideIndex) => {
    const active = slideIndex === index;
    slide.setAttribute('aria-hidden', String(!active));
    const image = slide.querySelector('img');
    if (active) image?.setAttribute(imageAttribute, '');
    else image?.removeAttribute(imageAttribute);
  });
}

function resetProductPopupGallery(state) {
  window.clearTimeout(state.galleryScrollTimer);
  state.galleryScrollTimer = 0;
  state.galleryScrollTarget = null;
  const track = state.modal?.querySelector?.('[data-hp-popup-track]');
  const swipe = state.swipe;
  state.swipe = null;
  if (!track) return;
  track.style.scrollSnapType = '';
  track.classList.remove('is-dragging');
  if (swipe && track.hasPointerCapture?.(swipe.pointerId)) track.releasePointerCapture(swipe.pointerId);
}

function scrollProductPopupGallery(state, index, options = {}) {
  const track = state.modal?.querySelector('[data-hp-popup-track]');
  if (!track?.clientWidth) return;
  state.galleryTrackWidth = track.clientWidth;
  window.clearTimeout(state.galleryScrollTimer);
  const left = index * track.clientWidth;
  const instant = options.instant === true || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  state.galleryScrollTarget = instant || Math.abs(track.scrollLeft - left) < 1 ? null : index;
  track.scrollTo({ left, behavior: state.galleryScrollTarget === null ? 'auto' : 'smooth' });
}

function bindProductPopupGallery(modal, state, getImages, showImage, isActive) {
  const track = modal.querySelector('[data-hp-popup-track]');
  if (!track) return;
  const sync = (settled = false) => {
    const images = getImages();
    if (!isActive() || !track.clientWidth || !images.length) return;
    if (state.galleryTrackWidth !== track.clientWidth) {
      resetProductPopupGallery(state);
      scrollProductPopupGallery(state, state.activeImageIndex, { instant: true });
      return;
    }
    if (state.swipe) return;
    // Keep an arrow/dot destination selected while smooth scrolling crosses
    // intermediate slides. A new touch or drag takes over immediately.
    if (state.galleryScrollTarget != null) {
      if (!settled) return;
      state.galleryScrollTarget = null;
    }
    const index = Math.max(0, Math.min(images.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
    if (index !== state.activeImageIndex) showImage(index, { scroll: false });
  };
  const settle = () => {
    window.clearTimeout(state.galleryScrollTimer);
    sync(true);
  };
  const interrupt = () => {
    if (!isActive()) return;
    window.clearTimeout(state.galleryScrollTimer);
    if (state.galleryScrollTarget != null) track.scrollTo({ left: track.scrollLeft, behavior: 'auto' });
    state.galleryScrollTarget = null;
    sync(true);
  };
  track.addEventListener('scroll', () => {
    sync();
    window.clearTimeout(state.galleryScrollTimer);
    state.galleryScrollTimer = window.setTimeout(settle, 160);
  }, { passive: true });
  track.addEventListener('scrollend', settle);
  track.addEventListener('wheel', interrupt, { passive: true });
  track.addEventListener('pointerdown', (event) => {
    if (!isActive() || event.isPrimary === false) return;
    interrupt();
    // Touch, pen and trackpads use native scroll/snap. Mouse dragging uses
    // that same scroll position, so only the images move under the controls.
    if (event.pointerType !== 'mouse' || event.button !== 0 || getImages().length < 2) return;
    state.swipe = {
      pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      lastX: event.clientX, startScrollLeft: track.scrollLeft,
      startIndex: state.activeImageIndex, swiping: false,
    };
    track.setPointerCapture?.(event.pointerId);
  });
  track.addEventListener('pointermove', (event) => {
    const swipe = state.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    swipe.lastX = event.clientX;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = event.clientY - swipe.startY;
    if (!swipe.swiping && Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      swipe.swiping = true;
      track.style.scrollSnapType = 'none';
      track.classList.add('is-dragging');
    }
    if (!swipe.swiping) return;
    event.preventDefault();
    track.scrollLeft = swipe.startScrollLeft - deltaX;
  }, { passive: false });
  const finish = (event, cancelled = false) => {
    const swipe = state.swipe;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    const deltaX = swipe.lastX - swipe.startX;
    resetProductPopupGallery(state);
    if (!swipe.swiping) return;
    const moved = Math.abs(deltaX) >= Math.min(48, track.clientWidth * .15);
    const index = swipe.startIndex + (!cancelled && moved ? (deltaX < 0 ? 1 : -1) : 0);
    showImage(Math.max(0, Math.min(getImages().length - 1, index)), { instant: cancelled });
  };
  track.addEventListener('pointerup', (event) => finish(event));
  track.addEventListener('pointercancel', (event) => finish(event, true));
  track.addEventListener('dragstart', (event) => event.preventDefault());
  const align = () => {
    // Browser chrome and image loading can change only the track's height.
    // Do not interrupt native momentum or an arrow animation unless its
    // horizontal slide geometry actually changed.
    if (!isActive() || !track.clientWidth || state.galleryTrackWidth === track.clientWidth) return;
    resetProductPopupGallery(state);
    scrollProductPopupGallery(state, state.activeImageIndex, { instant: true });
  };
  if (typeof ResizeObserver === 'function') new ResizeObserver(align).observe(track);
  else window.addEventListener('resize', align, { passive: true });
}

function bindRecommendationQuickViewSwipe(modal) {
  bindProductPopupGallery(modal, recommendationQuickViewState,
    () => getRecommendationQuickViewImages(recommendationQuickViewState.activeProduct),
    showRecommendationQuickViewImage,
    () => modal.classList.contains('is-open') && modal.getAttribute('data-hp-modal-view') === 'images');
}

function closeRecommendationQuickView() {
  recommendationQuickViewState.requestId += 1;
  resetProductPopupGallery(recommendationQuickViewState);
  const modal = recommendationQuickViewState.modal;
  if (!modal) return;

  const wasOpen = modal.classList.contains('is-open') || modal.getAttribute('aria-hidden') === 'false';
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  recommendationQuickViewState.activeProduct = null;
  recommendationQuickViewState.activeImageIndex = 0;
  recommendationQuickViewState.swipe = null;
  if (wasOpen) unlockRecommendationQuickViewScroll();
  if (wasOpen) recommendationQuickViewState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function getModalScrollbarCompensation() {
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function applyModalScrollLock(state) {
  state.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
  state.previousDocumentOverflow = document.documentElement.style.overflow;
  state.previousDocumentOverscrollBehavior = document.documentElement.style.overscrollBehavior;
  state.previousDocumentScrollbarGutter = document.documentElement.style.scrollbarGutter;
  state.previousDocumentScrollBehavior = document.documentElement.style.scrollBehavior;
  state.previousBodyOverflow = document.body.style.overflow;
  state.previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;
  state.previousBodyPaddingRight = document.body.style.paddingRight;
  state.previousBodyPosition = document.body.style.position;
  state.previousBodyTop = document.body.style.top;
  state.previousBodyLeft = document.body.style.left;
  state.previousBodyRight = document.body.style.right;
  state.previousBodyWidth = document.body.style.width;
  state.previousBodyScrollBehavior = document.body.style.scrollBehavior;

  const scrollbarWidth = getModalScrollbarCompensation();
  const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
  const supportsStableScrollbarGutter = window.CSS?.supports?.('scrollbar-gutter: stable') === true;

  document.documentElement.style.scrollbarGutter = 'stable';
  document.documentElement.style.overflow = 'hidden';
  document.documentElement.style.overscrollBehavior = 'none';
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.overflow = 'hidden';
  document.body.style.overscrollBehavior = 'none';
  document.body.style.scrollBehavior = 'auto';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${state.scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  if (scrollbarWidth > 0 && !supportsStableScrollbarGutter) {
    document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
  }
}

function releaseModalScrollLock(state) {
  const lockedBodyTop = Number.parseFloat(document.body.style.top || '');
  const nextScrollY = Math.max(
    0,
    Number.isFinite(state.scrollY) && state.scrollY > 0
      ? state.scrollY
      : Number.isFinite(lockedBodyTop)
        ? Math.abs(lockedBodyTop)
        : 0
  );
  const restoreScrollPosition = () => {
    window.scrollTo(0, nextScrollY);
    if (document.scrollingElement) document.scrollingElement.scrollTop = nextScrollY;
    document.documentElement.scrollTop = nextScrollY;
    document.body.scrollTop = nextScrollY;
  };
  const restoreScrollBehavior = () => {
    document.documentElement.style.scrollBehavior = state.previousDocumentScrollBehavior || '';
    document.body.style.scrollBehavior = state.previousBodyScrollBehavior || '';
  };
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.scrollBehavior = 'auto';
  document.documentElement.style.overflow = state.previousDocumentOverflow || '';
  document.documentElement.style.overscrollBehavior = state.previousDocumentOverscrollBehavior || '';
  document.documentElement.style.scrollbarGutter = state.previousDocumentScrollbarGutter || '';
  document.body.style.overflow = state.previousBodyOverflow || '';
  document.body.style.overscrollBehavior = state.previousBodyOverscrollBehavior || '';
  document.body.style.paddingRight = state.previousBodyPaddingRight || '';
  document.body.style.position = state.previousBodyPosition || '';
  document.body.style.top = state.previousBodyTop || '';
  document.body.style.left = state.previousBodyLeft || '';
  document.body.style.right = state.previousBodyRight || '';
  document.body.style.width = state.previousBodyWidth || '';
  restoreScrollPosition();
  window.requestAnimationFrame(restoreScrollPosition);
  window.setTimeout(restoreScrollPosition, 80);
  window.setTimeout(restoreScrollPosition, 260);
  window.setTimeout(restoreScrollBehavior, 320);
}

function lockRecommendationQuickViewScroll() {
  applyModalScrollLock(recommendationQuickViewState);
}

function unlockRecommendationQuickViewScroll() {
  releaseModalScrollLock(recommendationQuickViewState);
}

function bindProductImageModal() {
  if (productImageModalBound) return;

  productImageModalBound = true;
  document.addEventListener('click', handleProductImageModalClick);
  document.addEventListener('keydown', handleProductImageModalKeydown);
  window.matchMedia(PRODUCT_IMAGE_POPUP_MOBILE_QUERY).addEventListener('change', (event) => {
    if (event.matches) closeProductImageModal();
  });
}

function isProductImagePopupEnabled() {
  return !window.matchMedia(PRODUCT_IMAGE_POPUP_MOBILE_QUERY).matches;
}

function handleProductImageModalClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const close = target.closest('[data-hp-product-image-close]');
  if (close) {
    event.preventDefault();
    closeProductImageModal();
    return;
  }

  const prev = target.closest('[data-hp-product-image-prev]');
  if (prev) {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex - 1);
    return;
  }

  const next = target.closest('[data-hp-product-image-next]');
  if (next) {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex + 1);
    return;
  }

  const dot = target.closest('[data-hp-product-image-dot]');
  if (dot) {
    event.preventDefault();
    showProductImageModalImage(Number(dot.getAttribute('data-index') || 0));
    return;
  }

  const modal = productImageModalState.modal;
  if (modal?.classList.contains('is-open') && target.closest('[data-hp-product-image-modal]') && !target.closest('.hp-product-modal__content')) {
    event.preventDefault();
    closeProductImageModal();
    return;
  }

  if (target.closest('[data-hp-product-image-modal]')) return;

  const productInfo = target.closest(PRODUCT_PAGE_SELECTOR);
  const imageContainer = target.closest('.product-information__media .product-media-container--image');
  if (!(productInfo instanceof HTMLElement) || !(imageContainer instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();
  openProductImageModal(imageContainer, target);
}

function handleProductImageModalKeydown(event) {
  if (document.querySelector('dialog:modal')) return;

  const modal = productImageModalState.modal;
  if (!modal?.classList.contains('is-open')) return;

  if (event.key === 'Tab') {
    const content = modal.querySelector('.hp-product-modal__content');
    if (content instanceof HTMLElement) trapProductInfoOverlayFocus(event, content);
    return;
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    closeProductImageModal();
  }
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex - 1);
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    showProductImageModalImage(productImageModalState.activeImageIndex + 1);
  }
}

function openProductImageModal(container, opener) {
  // The inline gallery is the image viewer on phones, including landscape.
  if (!isProductImagePopupEnabled()) return;
  const images = getProductGalleryImages(container);
  if (!images.length) return;

  const modal = ensureProductImageModal();
  const activeIndex = Math.max(0, getProductImageIndex(container, images));
  productImageModalState.images = images;
  productImageModalState.activeImageIndex = activeIndex;
  productImageModalState.lastFocusedElement = opener instanceof HTMLElement ? opener : document.activeElement instanceof HTMLElement ? document.activeElement : null;
  productImageModalState.swipe = null;

  resetProductPopupGallery(productImageModalState);
  renderProductPopupGallery(modal, images);
  renderProductImageModalDots(images, activeIndex);
  lockProductImageModalScroll();
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  showProductImageModalImage(activeIndex, { instant: true });
  modal.querySelector('.hp-product-modal__content')?.focus?.({ preventScroll: true });
}

function getProductGalleryImages(activeContainer) {
  const gallery = activeContainer.closest('media-gallery');
  if (!(gallery instanceof HTMLElement)) return [];

  const containers = Array.from(
    gallery.querySelectorAll('.media-gallery__grid .product-media-container--image, slideshow-slide.product-media-container--image')
  );
  const seen = new Set();

  return containers.map(getProductImageDataFromContainer).filter((image) => {
    if (!image?.src || seen.has(image.key)) return false;
    seen.add(image.key);
    return true;
  });
}

function getProductImageDataFromContainer(container) {
  if (!(container instanceof HTMLElement)) return null;

  const image = container.querySelector('.product-media__image');
  if (!(image instanceof HTMLImageElement)) return null;

  const mediaId = container.querySelector('.product-media')?.getAttribute('data-media-id') || '';
  const fullSource = image.dataset.maxResolution || image.currentSrc || image.src || '';
  const previewSource = image.currentSrc || image.src || fullSource;
  const src = getSizedShopifyImageSrc(fullSource, PRODUCT_IMAGE_FULL_WIDTH);
  const previewSrc = getSizedShopifyImageSrc(previewSource, PRODUCT_IMAGE_PREVIEW_WIDTH);

  return {
    key: mediaId || src || previewSrc,
    src,
    previewSrc,
    alt: image.alt || '',
  };
}

function getProductImageIndex(container, images) {
  const activeImage = getProductImageDataFromContainer(container);
  const activeIndex = images.findIndex((image) => image.key === activeImage?.key);
  return activeIndex >= 0 ? activeIndex : 0;
}

function ensureProductImageModal() {
  if (productImageModalState.modal instanceof HTMLElement) return productImageModalState.modal;

  const modal = document.createElement('div');
  modal.className = 'hp-product-modal hp-product-modal--image';
  modal.setAttribute('data-hp-product-image-modal', '');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <button type="button" class="hp-product-modal__backdrop" data-hp-product-image-close aria-label="Close product image"></button>
    <div class="hp-product-modal__content" role="dialog" tabindex="-1" aria-modal="true" aria-label="Product image viewer">
      <button type="button" class="hp-product-modal__close" data-hp-product-image-close aria-label="Close product image">&times;</button>
      <section class="hp-product-modal__gallery" aria-label="Product images">
        <div class="hp-product-modal__media">
          <button type="button" class="hp-product-modal__nav hp-product-modal__nav--prev" data-hp-product-image-prev aria-label="Previous product image" hidden>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 18 9 12l6-6"></path></svg>
          </button>
          <div class="hp-product-popup-track" data-hp-popup-track tabindex="-1"></div>
          <button type="button" class="hp-product-modal__nav hp-product-modal__nav--next" data-hp-product-image-next aria-label="Next product image" hidden>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>
          </button>
        </div>
        <div class="hp-product-modal__dots" data-hp-product-image-dots hidden></div>
      </section>
    </div>
  `;
  document.body.append(modal);
  productImageModalState.modal = modal;
  bindProductImageModalSwipe(modal);
  return modal;
}

function renderProductImageModalDots(images, activeIndex = 0) {
  const dots = ensureProductImageModal().querySelector('[data-hp-product-image-dots]');
  if (!(dots instanceof HTMLElement)) return;

  dots.hidden = images.length <= 1;
  dots.innerHTML = images.map((_, index) => (
    `<button type="button" class="hp-product-modal__dot${index === activeIndex ? ' is-active' : ''}" data-hp-product-image-dot data-index="${index}" aria-label="Show image ${index + 1}"></button>`
  )).join('');
}

function showProductImageModalImage(index, options = {}) {
  const modal = ensureProductImageModal();
  const images = productImageModalState.images;
  const nextIndex = Math.max(0, Math.min(images.length - 1, index));
  const image = getProductPopupSlideImage(modal, nextIndex);

  if (!(image instanceof HTMLImageElement)) return;
  if (!images.length) {
    image.hidden = true;
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    return;
  }

  const nextImage = images[nextIndex];
  productImageModalState.activeImageIndex = nextIndex;
  setModalImageSource(image, nextImage, 'min(96vw, 82rem)', PRODUCT_IMAGE_PREVIEW_WIDTH, PRODUCT_IMAGE_FULL_WIDTH);
  preloadNeighborImages(images, nextIndex);
  updateProductImageModalNavigationState(nextIndex);
  updateProductPopupSlides(modal, nextIndex, 'data-hp-product-image');
  if (options.scroll !== false) scrollProductPopupGallery(productImageModalState, nextIndex, options);

  modal.querySelectorAll('[data-hp-product-image-dot]').forEach((dot) => {
    dot.classList.toggle('is-active', Number(dot.getAttribute('data-index')) === nextIndex);
  });
}

function updateProductImageModalNavigationState(index = productImageModalState.activeImageIndex) {
  const modal = productImageModalState.modal;
  const images = productImageModalState.images;
  const prev = modal?.querySelector('[data-hp-product-image-prev]');
  const next = modal?.querySelector('[data-hp-product-image-next]');
  const currentIndex = Math.max(0, Math.min(images.length - 1, index));

  if (prev instanceof HTMLButtonElement) {
    prev.hidden = images.length <= 1;
    prev.disabled = currentIndex <= 0;
    prev.classList.toggle('is-muted', currentIndex <= 0);
  }
  if (next instanceof HTMLButtonElement) {
    next.hidden = images.length <= 1;
    next.disabled = currentIndex >= images.length - 1;
    next.classList.toggle('is-muted', currentIndex >= images.length - 1);
  }
}

function bindProductImageModalSwipe(modal) {
  bindProductPopupGallery(modal, productImageModalState,
    () => productImageModalState.images, showProductImageModalImage,
    () => modal.classList.contains('is-open'));
}

function closeProductImageModal() {
  resetProductPopupGallery(productImageModalState);
  const modal = productImageModalState.modal;
  if (!modal) return;

  const wasOpen = modal.classList.contains('is-open') || modal.getAttribute('aria-hidden') === 'false';
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  productImageModalState.images = [];
  productImageModalState.activeImageIndex = 0;
  productImageModalState.swipe = null;
  if (wasOpen) unlockProductImageModalScroll();
  if (wasOpen) productImageModalState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function lockProductImageModalScroll() {
  applyFlowPreservingModalScrollLock(productImageModalState);
  document.addEventListener('touchmove', blockProductImageModalBackgroundScroll, { passive: false, capture: true });
  document.addEventListener('wheel', blockProductImageModalBackgroundScroll, { passive: false, capture: true });
}

function unlockProductImageModalScroll() {
  document.removeEventListener('touchmove', blockProductImageModalBackgroundScroll, true);
  document.removeEventListener('wheel', blockProductImageModalBackgroundScroll, true);
  releaseFlowPreservingModalScrollLock(productImageModalState);
}

function blockProductImageModalBackgroundScroll(event) {
  if (!productImageModalState.modal?.classList.contains('is-open') || !event.cancelable) return;
  const target = event.target instanceof Element ? event.target : null;
  const track = target?.closest('[data-hp-popup-track]');
  // The image strip handles horizontal gestures natively; the root stays locked.
  if (track && productImageModalState.modal.contains(track) &&
      (event.type === 'touchmove' || Math.abs(event.deltaX || 0) > Math.abs(event.deltaY || 0))) return;
  event.preventDefault();
}

function getProductHelpData(productInfo) {
  const sectionRoot = productInfo?.closest?.('.shopify-section') || productInfo?.parentElement || document;
  const data = sectionRoot.querySelector('[data-hp-product-help-data]');
  return data instanceof HTMLElement ? data : null;
}

function shouldPreventHelpDrawerPanelScroll(panel, deltaY) {
  if (!(panel instanceof HTMLElement)) return true;
  if (panel.scrollHeight <= panel.clientHeight + 1) return true;

  const atTop = panel.scrollTop <= 1;
  const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
  return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
}

function bindProductHelpDrawerScrollGuard(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.hpScrollGuardBound === 'true') return;

  drawer.dataset.hpScrollGuardBound = 'true';
  let lastTouchY = 0;
  const getPanel = (event) =>
    event.target instanceof Element ? event.target.closest('.hp-product-help-drawer__panel') : null;

  drawer.addEventListener(
    'touchstart',
    (event) => {
      lastTouchY = event.touches?.[0]?.clientY || 0;
    },
    { passive: true }
  );

  drawer.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches?.[0];
      const panel = getPanel(event);
      if (!touch || !(panel instanceof HTMLElement)) {
        event.preventDefault();
        return;
      }

      const deltaY = lastTouchY - touch.clientY;
      lastTouchY = touch.clientY;
      if (Math.abs(deltaY) < 1) return;
      if (shouldPreventHelpDrawerPanelScroll(panel, deltaY)) event.preventDefault();
    },
    { passive: false }
  );

  drawer.addEventListener(
    'wheel',
    (event) => {
      const panel = getPanel(event);
      if (!(panel instanceof HTMLElement) || shouldPreventHelpDrawerPanelScroll(panel, event.deltaY)) {
        event.preventDefault();
      }
    },
    { passive: false }
  );
}

function bindProductHelpTrigger(productInfo) {
  if (!(productInfo instanceof HTMLElement) || productInfo.dataset.hpProductHelpReady === 'true') return;

  const data = getProductHelpData(productInfo);
  const builderTemplate = data?.querySelector('[data-hp-product-builder-trigger-template]');
  const triggerTemplate = data?.querySelector('[data-hp-product-help-trigger-template]');
  const content = productInfo.querySelector('.product-details .group-block-content');
  if (!(content instanceof HTMLElement)) return;
  if (!(builderTemplate instanceof HTMLTemplateElement) && !(triggerTemplate instanceof HTMLTemplateElement)) return;

  productInfo.dataset.hpProductHelpReady = 'true';
  if (
    (content.querySelector('[data-hp-product-builder-trigger]') || !(builderTemplate instanceof HTMLTemplateElement)) &&
    (content.querySelector('[data-hp-product-help-trigger]') || !(triggerTemplate instanceof HTMLTemplateElement))
  ) {
    return;
  }

  const trigger = triggerTemplate instanceof HTMLTemplateElement
    ? triggerTemplate.content.firstElementChild?.cloneNode(true)
    : null;
  const builderTrigger = builderTemplate instanceof HTMLTemplateElement
    ? builderTemplate.content.firstElementChild?.cloneNode(true)
    : null;

  const description = Array.from(content.children).find((child) =>
    child instanceof HTMLElement && (child.matches('rte-formatter.rte, .rte') || child.querySelector('rte-formatter.rte, .rte'))
  );
  const variantPicker = Array.from(content.children).find((child) => child instanceof HTMLElement && child.matches('variant-picker'));
  const productAddon = Array.from(content.children).find((child) =>
    child instanceof HTMLElement && child.matches('[data-hp-product-addon], product-addon-collection, .hp-product-addon')
  );

  const insertTriggersAfter = (reference) => {
    let anchor = reference;
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      anchor.insertAdjacentElement('afterend', builderTrigger);
      anchor = builderTrigger;
    }
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      anchor.insertAdjacentElement('afterend', trigger);
    }
  };

  if (productAddon instanceof HTMLElement) {
    insertTriggersAfter(productAddon);
  } else if (variantPicker instanceof HTMLElement) {
    insertTriggersAfter(variantPicker);
  } else if (description instanceof HTMLElement) {
    let helpTriggerAnchor = content.querySelector('[data-hp-product-help-trigger]');
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      description.insertAdjacentElement('afterend', trigger);
      helpTriggerAnchor = trigger;
    }
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]') && helpTriggerAnchor instanceof HTMLElement) {
      helpTriggerAnchor.before(builderTrigger);
    } else if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      description.insertAdjacentElement('afterend', builderTrigger);
    }
  } else {
    if (builderTrigger instanceof HTMLElement && !content.querySelector('[data-hp-product-builder-trigger]')) {
      content.append(builderTrigger);
    }
    if (trigger instanceof HTMLElement && !content.querySelector('[data-hp-product-help-trigger]')) {
      content.append(trigger);
    }
  }

  scheduleProductDetailHeightSync(productInfo);
}

function bindProductHelpDrawer() {
  if (productHelpDrawerBound) return;

  productHelpDrawerBound = true;
  document.addEventListener('click', handleProductHelpDrawerClick);
  document.addEventListener('keydown', handleProductHelpDrawerKeydown);
}

function handleProductHelpDrawerClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-hp-product-help-close]')) {
    event.preventDefault();
    closeProductHelpDrawer();
    return;
  }

  const opener = target.closest('[data-hp-product-help-open]');
  if (!opener) return;

  const productInfo = opener.closest(PRODUCT_PAGE_SELECTOR) || document.querySelector(PRODUCT_PAGE_SELECTOR);
  if (!(productInfo instanceof HTMLElement)) return;

  event.preventDefault();
  openProductHelpDrawer(productInfo, opener);
}

function handleProductHelpDrawerKeydown(event) {
  if (document.querySelector('dialog:modal')) return;

  const drawer = productHelpDrawerState.modal;
  if (!drawer?.classList.contains('is-open')) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    closeProductHelpDrawer();
    return;
  }

  if (event.key !== 'Tab') return;

  const panel = drawer.querySelector('.hp-product-help-drawer__panel');
  if (!(panel instanceof HTMLElement)) return;

  const focusableElements = getProductHelpDrawerFocusableElements(panel);
  if (!focusableElements.length) {
    event.preventDefault();
    panel.focus?.({ preventScroll: true });
    return;
  }

  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus({ preventScroll: true });
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus({ preventScroll: true });
  }
}

function getProductHelpDrawerFocusableElements(panel) {
  return Array.from(
    panel.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')
  ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
}

function ensureProductHelpDrawer(productInfo) {
  if (productHelpDrawerState.modal instanceof HTMLElement) return productHelpDrawerState.modal;

  const data = getProductHelpData(productInfo);
  const drawerTemplate = data?.querySelector('[data-hp-product-help-drawer-template]');
  if (!(drawerTemplate instanceof HTMLTemplateElement)) return null;

  const drawer = drawerTemplate.content.firstElementChild?.cloneNode(true);
  if (!(drawer instanceof HTMLElement)) return null;

  document.body.append(drawer);
  bindProductHelpDrawerScrollGuard(drawer);
  productHelpDrawerState.modal = drawer;
  return drawer;
}

function openProductHelpDrawer(productInfo, opener) {
  const drawer = ensureProductHelpDrawer(productInfo);
  if (!(drawer instanceof HTMLElement)) return;

  productHelpDrawerState.lastFocusedElement =
    opener instanceof HTMLElement ? opener : document.activeElement instanceof HTMLElement ? document.activeElement : null;

  if (!drawer.classList.contains('is-open')) applyModalScrollLock(productHelpDrawerState);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');

  const closeButton = drawer.querySelector('.hp-product-help-drawer__close');
  if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
}

function closeProductHelpDrawer() {
  const drawer = productHelpDrawerState.modal;
  if (!(drawer instanceof HTMLElement)) return;

  const wasOpen = drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false';
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (wasOpen) releaseModalScrollLock(productHelpDrawerState);
  if (wasOpen) productHelpDrawerState.lastFocusedElement?.focus?.({ preventScroll: true });
}

function formatRecommendationProductPrice(product) {
  const min = Number(product?.priceMin || 0);
  const max = Number(product?.priceMax || min);
  if (max > min) return `From ${formatRecommendationMoney(min)}`;
  return formatRecommendationMoney(min);
}

function formatRecommendationMoney(cents) {
  const amount = Number(cents || 0) / 100;
  const currency = window.Shopify?.currency?.active || 'SGD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

function initRecommendationQuickView(root = document) {
  enhanceRecommendationQuickViewButtons(root);
  bindRecommendationQuickView();
}

function syncProductPageScrollState() {
  productPageScrollStateFrame = 0;
  const hasProductPage = document.querySelector(PRODUCT_PAGE_SELECTOR) instanceof HTMLElement;
  const root = document.documentElement;

  if (!hasProductPage) {
    root.classList.remove('hp-product-page-scrollable');
    return;
  }

  // Cart, search, and menu drawers temporarily collapse the locked document's
  // scroll height to the viewport height. Keep the last valid page state while
  // a drawer owns the scroll lock so balanced product/FAQ spacing cannot flicker.
  if (root.classList.contains('is-scroll-locked')) return;

  const isScrollable = root.scrollHeight > root.clientHeight + 1;
  root.classList.toggle('hp-product-page-scrollable', isScrollable);
}

function scheduleProductPageScrollStateSync() {
  if (productPageScrollStateFrame) return;
  productPageScrollStateFrame = window.requestAnimationFrame(syncProductPageScrollState);
}

function initProductPageHelpers(root = document) {
  const productInfos = getProductInfoRoots(root);

  productInfos.forEach((productInfo) => {
    bindProductGalleryLayoutEnhancements(productInfo);
    bindProductGalleryMobileArrows(productInfo);
    bindProductVariantGalleryStability(productInfo);
    bindProductGuideVariantFit(productInfo);
    bindProductGalleryImagePreloading(productInfo);
    bindProductDetailHeightSync(productInfo);
    bindProductCartPressFeedback(productInfo);
    bindProductHelpTrigger(productInfo);
    bindProductAddonVariantSelectors(productInfo);
    bindProductAddonTitleRows(productInfo);
    bindProductTierVariantRefresh(productInfo);
    bindMemberTierInfo(productInfo);
    bindProductSizingInfo(productInfo);

    productInfo.querySelectorAll('.product-details rte-formatter.rte, .product-details .rte').forEach(bindDescriptionScrollRelease);
  });

  bindProductImageModal();
  bindProductHelpDrawer();
  initRecommendationQuickView(root);
  scheduleProductPageScrollStateSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductPageHelpers(), { once: true });
} else {
  initProductPageHelpers();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPageHelpers(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  productInfoOverlayElements.forEach((overlay) => {
    const controller = productInfoOverlayControllers.get(overlay);
    if (!controller?.productInfo || !event.target.contains(controller.productInfo)) return;
    controller.close({ restoreFocus: false, immediate: true });
    overlay.remove();
    productInfoOverlayElements.delete(overlay);
  });
});

window.addEventListener('load', scheduleProductPageScrollStateSync, { once: true });
window.addEventListener('resize', scheduleProductPageScrollStateSync, { passive: true });
window.addEventListener('pagehide', resetProductInfoOverlays);
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  resetProductInfoOverlays();
  initProductPageHelpers();
  scheduleProductPageScrollStateSync();
});
document.fonts?.ready.then(scheduleProductPageScrollStateSync);
